// analytics.jsx — default-on, first-party Mayanos product analytics with opt-out.
(function () {
  const CONSENT_VERSION = "2026-07-27";
  const CONSENT_KEY = "mayanos-analytics-consent-v1";
  const VISITOR_KEY = "mayanos-analytics-visitor-v1";
  const SESSION_KEY = "mayanos-analytics-session-v1";
  const FIRST_SEEN_KEY = "mayanos-analytics-first-seen-v1";
  const SESSION_STARTED_KEY = "mayanos-analytics-session-started-v1";
  const originalFetch = window.fetch.bind(window);
  let workspaceTimer = null;
  let lastSearch = { key: "", at: 0 };
  let sessionStartPromise = null;
  let lastPageView = { path: "", at: 0 };

  const randomId = () => {
    if (crypto.randomUUID) return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(24));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  };

  function readConsent() {
    try {
      const parsed = JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
      if (!parsed || !["accepted", "declined"].includes(parsed.status)) {
        return { status: "accepted", version: CONSENT_VERSION, defaultOn: true };
      }
      if (parsed.status === "accepted" && parsed.version !== CONSENT_VERSION) {
        return { status: "accepted", version: CONSENT_VERSION, defaultOn: true };
      }
      return parsed;
    } catch {
      return { status: "accepted", version: CONSENT_VERSION, defaultOn: true };
    }
  }

  function writeConsent(status) {
    const next = { status, version: CONSENT_VERSION, updatedAt: Date.now() };
    try { localStorage.setItem(CONSENT_KEY, JSON.stringify(next)); } catch {}
    window.dispatchEvent(new CustomEvent("mayanos-analytics-consent", { detail: next }));
    return next;
  }

  function storedId(key, storage) {
    try {
      let value = storage.getItem(key);
      if (!value) { value = randomId(); storage.setItem(key, value); }
      return value;
    } catch {
      return randomId();
    }
  }

  function routeName() {
    const route = (location.pathname.split("/")[1] || "home").toLowerCase();
    return route === "admin" ? "analytics" : route === "r" ? "reader" : route;
  }

  function currentContext(properties = {}) {
    const current = routeName() === "reader" ? window.SICHA || {} : {};
    return {
      route: routeName(),
      path: `${location.pathname}${location.search}`,
      referrer: document.referrer || "",
      bookRef: properties.bookRef ?? current._liveRef ?? current._liveTargetRef ?? "",
      collection: properties.collection ?? current.collection ?? "",
    };
  }

  function referrerDomain() {
    if (!document.referrer) return "Direct";
    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === location.origin ? "Internal" : referrer.hostname.toLowerCase();
    } catch {
      return "Unknown";
    }
  }

  function sessionProperties() {
    let firstSeen = 0;
    try { firstSeen = Number(localStorage.getItem(FIRST_SEEN_KEY) || 0); } catch {}
    const now = Date.now();
    if (!firstSeen) {
      firstSeen = now;
      try { localStorage.setItem(FIRST_SEEN_KEY, String(firstSeen)); } catch {}
    }
    return {
      browserStatus: firstSeen === now ? "new" : "returning",
      entryPage: location.pathname || "/",
      referrerDomain: referrerDomain(),
    };
  }

  async function ensureSessionStarted() {
    try { if (sessionStorage.getItem(SESSION_STARTED_KEY) === CONSENT_VERSION) return; } catch {}
    if (!sessionStartPromise) sessionStartPromise = track("session_started", sessionProperties(), { skipSessionStart: true }).then((ok) => {
      if (ok) { try { sessionStorage.setItem(SESSION_STARTED_KEY, CONSENT_VERSION); } catch {} }
      return ok;
    }).finally(() => { sessionStartPromise = null; });
    return sessionStartPromise;
  }

  async function track(eventName, properties = {}, options = {}) {
    if (readConsent().status !== "accepted") return false;
    const context = options.context || currentContext(properties || {});
    const occurredAt = Date.now();
    if (eventName === "page_view") {
      if (lastPageView.path === context.path && occurredAt - lastPageView.at < 1500) return false;
      lastPageView = { path: context.path, at: occurredAt };
    }
    if (eventName !== "session_started" && !options.skipSessionStart) await ensureSessionStarted();
    const payload = {
      eventName,
      occurredAt,
      visitorId: storedId(VISITOR_KEY, localStorage),
      sessionId: storedId(SESSION_KEY, sessionStorage),
      consentVersion: CONSENT_VERSION,
      analyticsEnabled: true,
      route: context.route,
      path: context.path,
      referrer: context.referrer,
      bookRef: context.bookRef,
      collection: context.collection,
      searchText: options.searchText || "",
      properties: properties || {},
      workspaceSnapshot: options.workspaceSnapshot,
    };
    try {
      const response = await originalFetch("/bff/analytics/events", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Mayanos-Analytics": "1",
        },
        body: JSON.stringify(payload),
        keepalive: !options.workspaceSnapshot,
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  async function setConsent(status) {
    const previous = readConsent().status;
    const next = writeConsent(status);
    if (status === "accepted" && previous !== "accepted") {
      track("page_view", { settingsEntry: true });
      scheduleWorkspaceSnapshot();
    }
    return next;
  }

  async function deleteStoredData() {
    const visitorId = storedId(VISITOR_KEY, localStorage);
    writeConsent("declined");
    try {
      const response = await originalFetch("/bff/analytics/events", {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-Mayanos-Analytics": "1",
        },
        body: JSON.stringify({ visitorId }),
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  function scheduleWorkspaceSnapshot() {
    if (readConsent().status !== "accepted") return;
    clearTimeout(workspaceTimer);
    workspaceTimer = setTimeout(() => {
      const workspace = window.WS && window.WS.get ? window.WS.get() : null;
      if (!workspace) return;
      const active = window.WS.active ? window.WS.active() : null;
      track("workspace_snapshot", {
        sheets: Array.isArray(workspace.sheets) ? workspace.sheets.length : 0,
        activeTitle: active && active.title || "",
        blocks: active && Array.isArray(active.blocks) ? active.blocks.length : 0,
      }, { workspaceSnapshot: workspace });
    }, 4_000);
  }

  window.fetch = function analyticsAwareFetch(input, init) {
    const startedAt = performance.now();
    const requestContext = currentContext();
    const purpose = init?.mayanosRequestPurpose || "";
    const sourceAttemptId = init?.mayanosSourceAttempt || "";
    let searchEvent = null;
    let requestUrl = null;
    try {
      const raw = typeof input === "string" ? input : input && input.url;
      const url = raw ? new URL(raw, location.origin) : null;
      requestUrl = url;
      if (url && url.origin === location.origin) {
        const query = url.searchParams.get("q");
        const isSearch = url.pathname === "/bff/search" || url.pathname === "/bff/topics";
        if (isSearch && query && query.trim().length >= 2) {
          const key = `${url.pathname}:${query.trim()}`;
          const now = Date.now();
          if (lastSearch.key !== key || now - lastSearch.at > 1_500) {
            lastSearch = { key, at: now };
            searchEvent = { surface: url.pathname === "/bff/topics" ? "topics" : routeName(), query: query.trim() };
          }
        }
      }
    } catch {}
    return originalFetch(input, init).then((response) => {
      if (searchEvent) track("search", { surface: searchEvent.surface, durationMs: Math.round(performance.now() - startedAt), status: response.status }, { searchText: searchEvent.query, context: requestContext });
      if (requestUrl && requestUrl.origin === location.origin) {
        const textRequest = /^\/bff\/(text|sources|toc)(\/|$)/.test(requestUrl.pathname);
        if (response.status >= 500 || (textRequest && response.status === 404)) {
          track("error", {
            errorType: textRequest && response.status === 404 ? "missing_text" : "loading_failure",
            endpoint: requestUrl.pathname, purpose, sourceAttemptId,
            status: response.status,
          }, { context: requestContext });
        }
      }
      return response;
    }).catch((error) => {
      if (requestUrl && requestUrl.origin === location.origin) {
        const message = error && error.message || "Network request failed";
        const isAbort = error && error.name === "AbortError" || /\babort(?:ed|error)?\b/i.test(message);
        if (isAbort) throw error;
        track("error", {
          errorType: "network_failure",
          endpoint: requestUrl.pathname, purpose, sourceAttemptId,
          message,
        }, { context: requestContext });
      }
      throw error;
    });
  };

  window.addEventListener("error", (event) => {
    track("error", { errorType: "client_error", message: event.message || "Client error" });
  });
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    track("error", { errorType: "unhandled_rejection", message: reason && reason.message || String(reason || "Unhandled rejection") });
  });
  window.addEventListener("mayanos-workspace-change", scheduleWorkspaceSnapshot);
  window.MAYANOS_ANALYTICS = {
    consentVersion: CONSENT_VERSION,
    preference: readConsent,
    setConsent,
    deleteStoredData,
    track,
    context: currentContext,
    scheduleWorkspaceSnapshot,
  };
  if (readConsent().status === "accepted") scheduleWorkspaceSnapshot();
})();

const { useEffect: useEffectAN, useState: useStateAN } = React;

function useAnalyticsPreference() {
  const read = () => window.MAYANOS_ANALYTICS ? window.MAYANOS_ANALYTICS.preference() : { status: "accepted", defaultOn: true };
  const [preference, setPreference] = useStateAN(read);
  useEffectAN(() => {
    const update = () => setPreference(read());
    window.addEventListener("mayanos-analytics-consent", update);
    return () => window.removeEventListener("mayanos-analytics-consent", update);
  }, []);
  return preference;
}

function AnalyticsPreferenceToggle({ compact = false }) {
  const preference = useAnalyticsPreference();
  const enabled = preference.status !== "declined";
  return (
    <div className={`analytics-setting${compact ? " compact" : ""}`}>
      <span>
        <b>Usage analytics</b>
        <small>{enabled ? "On — helps improve Mayanos" : "Off — this browser is not tracked"}</small>
      </span>
      <button
        type="button"
        className="analytics-switch"
        role="switch"
        aria-checked={enabled}
        aria-label="Usage analytics"
        onClick={() => window.MAYANOS_ANALYTICS.setConsent(enabled ? "declined" : "accepted")}
      >
        <i />
      </button>
    </div>
  );
}

function PrivacyPage() {
  const preference = useAnalyticsPreference();
  const [deleting, setDeleting] = useStateAN(false);
  const [message, setMessage] = useStateAN("");
  const accept = () => window.MAYANOS_ANALYTICS.setConsent("accepted");
  const decline = () => window.MAYANOS_ANALYTICS.setConsent("declined");
  const remove = async () => {
    setDeleting(true); setMessage("");
    const ok = await window.MAYANOS_ANALYTICS.deleteStoredData();
    setDeleting(false);
    setMessage(ok ? "Detailed raw analytics linked to this browser and signed-in account were deleted. Previously created aggregate counts remain." : "Tracking is off. Stored-data deletion could not be confirmed right now.");
  };
  return (
    <section className="privacy-page">
      <header className="privacy-hero">
        <span>Privacy & analytics</span>
        <h1>A clear account of what Mayanos records</h1>
        <p>Detailed analytics is on by default to help improve Mayanos. You can turn it off at any time in Settings or below.</p>
      </header>
      <section className="privacy-grid">
        <article>
          <h2>Detailed analytics</h2>
          <p>While detailed analytics is on, Mayanos records raw events for 90 days. These can include:</p>
          <ul>
            <li>Pages, collections, books, texts, topics, sources, reader controls, printing, exports, and sharing actions.</li>
            <li>The complete text of searches.</li>
            <li>Research workspace snapshots, including sheet titles, source selections, custom text, and notes.</li>
            <li>Raw IP address, browser user agent, device class, and broad country supplied by the hosting network. Mayanos does not separately store precise location, region, or city.</li>
            <li>For signed-in readers: an internal account identifier, name, and email address.</li>
          </ul>
        </article>
        <article>
          <h2>Retention and purpose</h2>
          <p>Raw events expire after 90 days. Daily aggregate counts—such as visits per day, popular sections, devices, and countries—are retained long-term. Aggregate reports do not retain workspace contents, notes, searches, names, emails, or raw IP addresses. Aggregate counts already created are not removed when detailed raw data is deleted.</p>
          <p>The data is used to understand adoption, diagnose failures, prioritize corpus and interface work, and improve Mayanos. It is not sold, shared for advertising, or used for cross-site tracking.</p>
        </article>
        <article>
          <h2>Your controls</h2>
          <p>Current setting: <b>{preference.status === "declined" ? "Detailed analytics off" : "Detailed analytics on"}</b>.</p>
          <div className="privacy-actions">
            <button className="btn btn-primary" onClick={accept}>Allow detailed analytics</button>
            <button className="btn" onClick={decline}>Opt out</button>
            <button className="btn privacy-delete" disabled={deleting} onClick={remove}>{deleting ? "Deleting…" : "Delete my detailed analytics"}</button>
          </div>
          {message && <p className="privacy-status" role="status">{message}</p>}
        </article>
        <article>
          <h2>Essential account data</h2>
          <p>Opting out of analytics does not delete essential product data that you intentionally save—such as synchronized reading progress, bookmarks, settings, source sheets, or shared-workspace links. Those features are separate from analytics.</p>
          <p>Because reading and research activity can reveal religious interests, the detailed disclosure above explains exactly what is collected and the Settings control makes opting out persistent on this browser.</p>
        </article>
      </section>
    </section>
  );
}

Object.assign(window, { AnalyticsPreferenceToggle, PrivacyPage, useAnalyticsPreference });
